// packages/web-ui/src/components/kb-browser/group-by-hierarchy.ts
//
// Story W4-S5 (AC #3.2) — pure helper that groups KB entities by their
// `qualifiedName` parent path (everything except the entity's own short name)
// and stable-sorts within each group by KIND_ORDER + qualifiedName ascending.
//
// Top-level entities (qualifiedName has no `::`) land in the literal `(root)`
// group rendered first.

import type { KbEntity, KbEntityKind } from '@tessa-lean/base/api-schemas/index.js';

/**
 * Sort order for kinds within a group. Mirrors UX §5.3 "Parts → Ports → IF →
 * Conn → value-attribute" ordering. Re-exported here because W4-S4's
 * KbChangeTree constants are scoped to the changeset tree (different
 * KbEntityChange shape); deduplication is a follow-up.
 */
export const KIND_ORDER: ReadonlyArray<KbEntityKind> = [
  'part',
  'part-usage',
  'port',
  'interface',
  'interface-def',
  'connection',
  'connection-def',
  'value-attribute',
];

const KIND_RANK: Record<KbEntityKind, number> = {
  part: 0,
  'part-usage': 1,
  port: 2,
  interface: 3,
  'interface-def': 4,
  connection: 5,
  'connection-def': 6,
  'value-attribute': 7,
};

/** Sentinel group key for top-level entities with no parent path. */
export const ROOT_GROUP_KEY = '(root)';

export interface HierarchyGroup {
  /** Parent qualified-name (e.g. `Vehicle`) or `(root)` for top-level. */
  key: string;
  /** Entities sorted by kind then qualifiedName. */
  entities: KbEntity[];
}

/**
 * Groups entities by parent qualified-name path. Stable: groups are emitted
 * in first-encounter order (so callers see (root) → Vehicle → Vehicle::Drive
 * etc.) and entities within a group are sorted by `KIND_RANK` then
 * qualifiedName ascending.
 */
export function groupByHierarchy(entities: KbEntity[]): HierarchyGroup[] {
  const buckets = new Map<string, KbEntity[]>();
  const order: string[] = [];

  for (const entity of entities) {
    const parts = entity.qualifiedName.split('::');
    const parent = parts.length > 1 ? parts.slice(0, -1).join('::') : '';
    const key = parent.length === 0 ? ROOT_GROUP_KEY : parent;
    let bucket = buckets.get(key);
    if (bucket === undefined) {
      bucket = [];
      buckets.set(key, bucket);
      order.push(key);
    }
    bucket.push(entity);
  }

  for (const bucket of buckets.values()) {
    bucket.sort((a, b) => {
      const rankDiff = KIND_RANK[a.kind] - KIND_RANK[b.kind];
      if (rankDiff !== 0) return rankDiff;
      return a.qualifiedName.localeCompare(b.qualifiedName);
    });
  }

  return order.map((key): HierarchyGroup => {
    const entities = buckets.get(key);
    return { key, entities: entities ?? [] };
  });
}
